import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Mill,
  DataLayerId,
  SpeciesOverlayVariant,
  SupplyShedDriveTime,
} from '../../lib/types';
import { getMillById } from '../../lib/millsData';
import { useIsochrone } from '../../hooks/useIsochrone';
import { useNativeViewState } from '../../hooks/useNativeViewState';
import { useScenario } from '../../hooks/useScenario';
import { findScenarioById } from '../../lib/scenarios';
import { useBasemap } from '../../context/BasemapContext';
import { API_CONFIG } from '../../lib/apiConfig';
import {
  readDetailUrlParams,
  writeDetailUrlParams,
} from '../../lib/detailUrlParams';
import { DetailLeftPanel } from './DetailLeftPanel';
import { DetailMapView } from './DetailMapView';

export const DetailView: React.FC = () => {
  const { millId } = useParams<{ millId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const mill: Mill | null = location.state?.mill || (millId ? getMillById(millId) : null);
  const { basemap } = useBasemap();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default state — used both as the lazy-init fallback when URL params
  // are absent / malformed AND as the omit-when threshold so the URL
  // sync below strips params back to a clean URL when the user lands on
  // these defaults again. `layer` defaults to the active basemap's
  // preference (e.g. AGB basemap → aboveground_biomass) so the left
  // panel matches what the map is already showing on cold load.
  const detailDefaults = useMemo(
    () => ({
      layer: basemap.preferredLayerId ?? null,
      driveTime: 60 as SupplyShedDriveTime,
      agbYear: API_CONFIG.agbOverlay.maxYear,
      nativeViewActive: false,
      scenarioId: null,
      scenarioStep: 0,
    }),
    [basemap.preferredLayerId],
  );

  // Hydrate from URL on first render so a shared deep-link (e.g.
  // /detail/X?layer=fire_risk&dt=90&year=2018) reproduces the camera +
  // panel state the sender was viewing. Each field falls back to the
  // default when the param is absent or fails validation in
  // `readDetailUrlParams`. Captured once via lazy initialiser — the
  // sync effect below keeps URL in step thereafter.
  const initialFromUrl = useMemo(
    () => readDetailUrlParams(searchParams, detailDefaults),
    // Mount-only: deps intentionally omitted so a later URL change
    // (e.g. our own state-sync write) doesn't reset state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [selectedLayer, setSelectedLayer] = useState<DataLayerId | null>(
    initialFromUrl.layer,
  );
  const [selectedDriveTime, setSelectedDriveTime] = useState<SupplyShedDriveTime>(
    initialFromUrl.driveTime,
  );
  const [speciesOverlayVariant, setSpeciesOverlayVariant] = useState<SpeciesOverlayVariant>('dominant');
  const [agbYear, setAgbYear] = useState<number>(initialFromUrl.agbYear);
  const [soilCarbonDepth, setSoilCarbonDepth] = useState<30 | 100>(30);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  // Mobile bottom-sheet expanded state. Lifted to DetailView so external
  // events (per-crown native-view auto-collapse, scenario map-overlay
  // "Read in panel" handoff) can drive the sheet without DetailLeftPanel
  // and DetailMapView having to coordinate through context.
  const [mobileSheetExpanded, setMobileSheetExpanded] = useState(true);

  const scenario = useScenario();

  // Snapshot of the user's drive-time selection at the moment a scenario
  // started, so a mid-flow "End scenario" / "Stay & explore" can restore
  // the prior view instead of pinning the user to whatever drive time the
  // scenario happened to use. `null` means no scenario is active (or the
  // current scenario was entered cold via URL/router state with no prior
  // free-explore selection — in that case the snapshot is still the
  // hydrated default, which is the same answer).
  const preScenarioDriveTimeRef = useRef<SupplyShedDriveTime | null>(null);

  // Scenario hand-off — picks up a scenario whenever ExplorerView or the
  // "try another" affordance on the insight step hands one off via router
  // state. Watches location.state (not just mount) because React Router v6
  // re-uses DetailView across mill changes on the same route pattern, so
  // mill A → mill B with a new scenarioId doesn't unmount. The incoming-id
  // guard prevents restart loops when the effect re-runs for unrelated
  // reasons.
  const activeScenarioId = scenario.activeScenario?.id;
  useEffect(() => {
    const incomingId = location.state?.scenarioId as string | undefined;
    if (!incomingId || incomingId === activeScenarioId) return;
    const match = findScenarioById(incomingId);
    if (!match) return;
    preScenarioDriveTimeRef.current = selectedDriveTime;
    scenario.start(match);
    setSelectedDriveTime(match.driveTime);
    // `scenario` itself is a stable object of useCallback'd setters — pulling
    // it into deps would re-run this on every state change. The
    // activeScenarioId slice is all we need to recognise a changed hand-off.
    // selectedDriveTime is read inside the effect as a snapshot value;
    // including it in deps would re-fire the start branch every time the
    // user toggled the drive-time radio mid-scenario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, activeScenarioId]);

  // URL → scenario hydration on first mount. Lets a refresh mid-walkthrough
  // (or a shared deep-link like /detail/X?scenario=carbon_offense&step=2)
  // pick up the scenario at the right step instead of dropping the user
  // into free exploration. Yields to the router-state hand-off above when
  // both are present — the state hand-off carries fresher intent (the
  // user just clicked a card) than the captured URL.
  useEffect(() => {
    if (initialFromUrl.scenarioId === null) return;
    if (location.state?.scenarioId) return;
    const match = findScenarioById(initialFromUrl.scenarioId);
    if (!match) return;
    preScenarioDriveTimeRef.current = selectedDriveTime;
    scenario.start(match);
    setSelectedDriveTime(match.driveTime);
    if (initialFromUrl.scenarioStep > 0) {
      scenario.goToStep(initialFromUrl.scenarioStep);
    }
    // Mount-only by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Layer + drive-time hand-off — used by deep-link navigations (e.g. the
  // "View in California Sierra Nevada" CTA on the per-tree empty-state
  // panel) to land DetailView with a specific layer pre-selected and a
  // specific drive time. Same watcher pattern as the scenarioId hand-off
  // above; the replace-navigate strips the consumed values so a stale
  // state slice can't re-fire on the next render.
  useEffect(() => {
    const incomingLayerId = location.state?.layerId as DataLayerId | undefined;
    const incomingDriveTime = location.state?.driveTime as SupplyShedDriveTime | undefined;
    if (!incomingLayerId && !incomingDriveTime) return;
    if (incomingLayerId) setSelectedLayer(incomingLayerId);
    if (incomingDriveTime) setSelectedDriveTime(incomingDriveTime);
    navigate(location.pathname, {
      replace: true,
      state: mill ? { mill } : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Exiting a scenario must also clear `scenarioId` from router state.
  // Otherwise the effect above sees a stale `incomingId` after exit (the user
  // closed the scenario, but `location.state.scenarioId` is still set) and
  // re-starts it on the very next render — silently undoing the exit. We
  // replace the entry rather than push so the back button doesn't ferry the
  // user back into the same scenario they just left. Restore the
  // pre-scenario drive time so the user lands back on the shed they were
  // exploring before the scenario clobbered it.
  const handleScenarioExit = useCallback(() => {
    scenario.exit();
    if (preScenarioDriveTimeRef.current !== null) {
      setSelectedDriveTime(preScenarioDriveTimeRef.current);
      preScenarioDriveTimeRef.current = null;
    }
    navigate(location.pathname, {
      replace: true,
      state: mill ? { mill } : undefined,
    });
  }, [scenario, navigate, location.pathname, mill]);

  // "Back to supply sheds" off-ramp from the last scenario step. Calls
  // scenario.exit() before navigating so any future change that keeps
  // useScenario state alive across a route swap (lifting it to context,
  // persisting it, etc.) can't leak an active scenario into the next
  // mount. The route-change-on-the-next-tick concern that prevented this
  // call earlier doesn't apply here — we're navigating *off* /detail, so
  // the watcher effect that could have re-fired the scenario is leaving
  // with us. Pass `fromDetail` so LeftPanel scrolls the user to the mill
  // picker on arrival — same UX rationale as the regular Back button in
  // DetailLeftPanel.
  const handleScenarioExitToExplorer = useCallback(() => {
    scenario.exit();
    navigate('/', { state: { fromDetail: true } });
  }, [scenario, navigate]);

  // Derive the set of layers that should paint on the map. In scenario mode,
  // the current step declares the full set; in free exploration, it's just
  // whatever the user picked (or nothing). Last-added layer is treated as the
  // "primary" layer and drives UI chrome (legends, sliders, left panel).
  const activeLayers = useMemo<DataLayerId[]>(() => {
    if (scenario.currentStep) return scenario.currentStep.activeLayers;
    return selectedLayer ? [selectedLayer] : [];
  }, [scenario.currentStep, selectedLayer]);

  const primaryLayer: DataLayerId | null =
    scenario.currentStep ? activeLayers[activeLayers.length - 1] ?? null : selectedLayer;

  // Scenario overlay variant takes precedence over the user's toggle state
  // while scenario mode is active (preserves the user's preference on exit).
  // No cast required — overlayVariants.species_diversity is typed as
  // SpeciesOverlayVariant by ScenarioOverlayVariants in lib/types.ts.
  const effectiveSpeciesVariant: SpeciesOverlayVariant =
    scenario.currentStep?.overlayVariants?.species_diversity ?? speciesOverlayVariant;

  // Scenario steps that talk specifically about private timberlands flag the
  // forest_ownership variant as 'private_focus'. When set, DetailMapView
  // layers a black-mask PNG above the categorical FO overlay so family +
  // corporate pop and every other class dims. Free exploration leaves this
  // false — categorical render unchanged.
  const foPrivateFocus =
    scenario.currentStep?.overlayVariants?.forest_ownership === 'private_focus';

  // Carbon Offense scenario swaps the AGB year-tile pyramid for the
  // precomputed change PNG (smoothed-endpoint diff 2000-04 → 2021-25). When
  // this is on, DetailMapView suppresses the year-tile crossfade + slider
  // and paints the change overlay instead, with its own diverging legend.
  const agbChangeMode =
    scenario.currentStep?.overlayVariants?.aboveground_biomass === 'change';

  const supplyShed = useIsochrone(mill?.metadata?.code ?? null, selectedDriveTime);

  // "Native resolution" view for the per-tree-forest-attributes layer.
  // Toggled on by the left-panel button (which also flies the camera into
  // the demo window). The hook auto-clears the flag whenever the user
  // navigates to a different mill / drive time / layer so the cached
  // GeoJSON doesn't render off-target crowns against a new shed.
  const {
    active: nativeViewActive,
    toggle: handleToggleNativeView,
    activate: activateNativeView,
    deactivate: handleExitNativeView,
  } = useNativeViewState(millId, selectedDriveTime, primaryLayer);

  // If the URL hydrated `native=1` on first mount, fire the native-view
  // activation once so the camera flies into the demo window and the
  // crown polygons paint. Mount-only by design — runs after the hook's
  // state machine has settled. Idempotent: `activate()` is a no-op when
  // already active, so a re-mount caused by an unrelated re-render
  // doesn't re-fire the cinematic.
  useEffect(() => {
    if (initialFromUrl.nativeViewActive) {
      activateNativeView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync DetailView state → URL search params so the user's current
  // layer / drive time / AGB year / native-view flag / active scenario
  // step are captured in the address bar and survive copy-paste +
  // refresh. `replace: true` keeps the browser back stack from filling
  // up with intermediate states. The helper compares against the
  // previous string before calling so we don't trigger no-op router
  // updates each render.
  const activeScenarioIdForUrl = scenario.activeScenario?.id ?? null;
  const scenarioStepIndex = scenario.stepIndex;
  useEffect(() => {
    const { params, changed } = writeDetailUrlParams(
      searchParams,
      {
        layer: selectedLayer,
        driveTime: selectedDriveTime,
        agbYear,
        nativeViewActive,
        scenarioId: activeScenarioIdForUrl,
        scenarioStep: scenarioStepIndex,
      },
      detailDefaults,
    );
    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [
    selectedLayer,
    selectedDriveTime,
    agbYear,
    nativeViewActive,
    activeScenarioIdForUrl,
    scenarioStepIndex,
    detailDefaults,
    searchParams,
    setSearchParams,
  ]);

  // Auto-activate the per-crown native view when a scenario step asks for
  // it (today: Individual Tree step 3, both mobile and desktop). Lets
  // "Next →" take the user straight from step 2's canopy-height vantage
  // into the cinematic fly + crown polygons, instead of forcing a manual
  // button click. The CTA tile in ScenarioPanel still renders so users
  // can exit native view from the panel. The activate call is idempotent,
  // so re-renders inside the same step are no-ops.
  useEffect(() => {
    if (scenario.currentStep?.autoActivateNativeView) {
      activateNativeView();
    }
  }, [scenario.currentStep, activateNativeView]);

  if (!mill) {
    return (
      <div className="flex items-center justify-center h-full bg-figma-panel">
        <div className="text-center space-y-4">
          <p className="text-neutral-400 font-roboto text-body">Mill not found</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-figma-lime hover:underline font-roboto text-body"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative md:flex md:flex-row h-full w-full bg-figma-surface absolute inset-0">
      <DetailLeftPanel
        mill={mill}
        selectedLayer={primaryLayer}
        selectedDriveTime={selectedDriveTime}
        onLayerChange={(layer) => setSelectedLayer(layer)}
        onDriveTimeChange={setSelectedDriveTime}
        isCollapsed={isPanelCollapsed}
        onToggleCollapse={() => setIsPanelCollapsed(!isPanelCollapsed)}
        isochroneLoading={supplyShed.isLoading}
        agbYear={agbYear}
        soilCarbonDepth={soilCarbonDepth}
        onSoilCarbonDepthChange={setSoilCarbonDepth}
        scenario={{
          ...scenario,
          exit: handleScenarioExit,
          exitToExplorer: handleScenarioExitToExplorer,
        }}
        nativeViewActive={nativeViewActive}
        onToggleNativeView={handleToggleNativeView}
        mobileSheetExpanded={mobileSheetExpanded}
        onMobileSheetExpandedChange={setMobileSheetExpanded}
      />

      <div className="absolute inset-0 md:relative md:inset-auto md:order-2 md:h-full md:flex-1 overflow-hidden">
        <DetailMapView
          mill={mill}
          selectedLayer={primaryLayer}
          activeLayers={activeLayers}
          selectedDriveTime={selectedDriveTime}
          supplyShed={supplyShed}
          onLayerChange={(layer) => setSelectedLayer(layer)}
          speciesOverlayVariant={effectiveSpeciesVariant}
          onSpeciesOverlayVariantChange={setSpeciesOverlayVariant}
          agbYear={agbYear}
          onAgbYearChange={setAgbYear}
          soilCarbonDepth={soilCarbonDepth}
          scenarioMapFocus={scenario.currentStep?.mapFocus ?? null}
          foPrivateFocus={foPrivateFocus}
          agbChangeMode={agbChangeMode}
          nativeViewActive={nativeViewActive}
          onExitNativeView={handleExitNativeView}
          scenarioStep={scenario.currentStep}
          activeScenario={scenario.activeScenario}
          scenarioStepIndex={scenario.stepIndex}
          scenarioTotalSteps={scenario.totalSteps}
          scenarioIsFirstStep={scenario.isFirstStep}
          scenarioIsLastStep={scenario.isLastStep}
          onScenarioPrev={scenario.prev}
          onScenarioNext={scenario.next}
          onOpenMobileSheet={() => setMobileSheetExpanded(true)}
          onOpenMobileSheetToInsight={() => {
            setMobileSheetExpanded(true);
            // Sheet expansion swaps the peek-bar render for the
            // fullscreen-panel render — ScenarioPanel + the
            // `<div data-scenario-insight>` wrapper aren't in the DOM
            // until after that commit. A single fixed delay was racy:
            // it worked on Carbon Offense (lighter step content) but
            // missed on Carbon Defense / Individual Tree where the
            // panel had more to render. Two RAFs flush the commit, then
            // poll up to 10× / 100 ms for the wrapper before giving up.
            const tryScroll = (attempts: number) => {
              const el = document.querySelector('[data-scenario-insight]');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
              }
              if (attempts > 0) {
                window.setTimeout(() => tryScroll(attempts - 1), 100);
              }
            };
            requestAnimationFrame(() => {
              requestAnimationFrame(() => tryScroll(10));
            });
          }}
        />
      </div>
    </div>
  );
};
